<template>
  <v-container fluid class="HazmatAuthorizationSites">
    <v-layout row wrap justify-space-between>
      <v-flex md3>
        <v-autocomplete 
          :items="allWarehousesOptions"
          v-model="hazmatWarehouse"
          :label="$t('warehouse')"
        />
      </v-flex>
      <v-flex md4>
        <v-btn 
          color="primary" 
          dark 
          @click="addAuthorizationSites"
        >
          {{ $t('addAuthorizationSites') }}
        </v-btn>          
      </v-flex>
      <v-flex md4>
      </v-flex>
    </v-layout>
    <v-data-table
      :headers="headers"
      :items="hazmatSitesTable"
      hide-actions
      :pagination.sync="pagination"
      :rows-per-page-items="rowPerPage"
      item-key="key"
    >
      <template v-slot:items="props">
        <td class="text-xs-left">
          <v-layout>
            <v-flex>{{ $t('siteName') }}: {{ props.item.siteName }}</v-flex>
          </v-layout>
          <v-layout>
            <v-flex>{{ $t('warehouse') }}: {{ props.item.warehouseName }}</v-flex>
          </v-layout>
        </td>
        <td class="text-xs-left">{{ props.item.authorizationId }}</td>
        <td class="text-xs-left">
          {{ abbrevToStates[props.item.state] || props.item.state }}
          {{ props.item.city }}
          {{ props.item.address1 || '' }}
          {{ props.item.address2 || '' }}
        </td>
        <td class="text-xs-left">{{ props.item.contact }}/{{ props.item.phone }}</td>
        <td class="text-xs-center">
          <v-layout row>
            <v-flex>
              <LoaderButton
                flat
                :promiseAwait="updateAuthorizationSites"
                :promiseItem="props.item"
              >
                <template v-slot:icon>
                  <v-icon>refresh</v-icon>
                </template>
              </LoaderButton>
            </v-flex>
            <v-flex>
              <v-btn 
                dark 
                flat 
                color="primary" 
                @click.stop="deleteAuthorizationSites(props.item)"
              >
                <v-icon>delete</v-icon>
              </v-btn>
            </v-flex>
          </v-layout>
        </td>
      </template>
    </v-data-table>

    <FormSubmitPopup
      ref="form"
      :title="$t('addAuthorizationSites')"
      v-model="sitesModel.show"
      v-if="sitesModel.show"
      :rightMethod="submit"
      :rightButtonText="$t('add')"
      :rightButtonLoading="sitesModel.loading"
      :leftMethod="() => sitesModel.show = false"
      hasAlert
      medium>
      <template v-slot:input>
        <v-layout justify-space-between align-baseline>
          <v-flex md4>
            <v-autocomplete 
              :items="warehousesOptions"
              v-model="sitesModel.warehouseKey"
              :label="$t('warehouse')"
              :rules="[fieldIsRequired('warehouse')]"
              @change="getSitesOptions"
            />
          </v-flex>
          <v-flex md5>
            <v-autocomplete
              :items="sitesModel.sitesOptions"
              v-model="sitesModel.siteKey"
              :label="$t('warehouseSite')"
              :rules="[fieldIsRequired('warehouse site')]"
            />
          </v-flex>
        </v-layout>
        <v-layout>
          <v-flex md4>
            <v-text-field
              :label="$t('authorizationId')"
              v-model="sitesModel.authorizationId"
              :rules="[fieldIsRequired('authorization id')]"
            ></v-text-field>
          </v-flex>
        </v-layout>
      </template>
    </FormSubmitPopup>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import PaginationController from './PaginationController'
import OP950DocumentsWarehouseSiteWidget from './OP950DocumentsWarehouseSiteWidget'
import FormSubmitPopup from './FormSubmitPopup'
import { checkRules } from '@/utils/tools'
import LoaderButton from './LoaderButton'

export default {
  name: 'HazmatAuthorizationSites',
  mixins: [checkRules],
  components: {
    PaginationController,
    FormSubmitPopup,
    OP950DocumentsWarehouseSiteWidget,
    LoaderButton
  },
  data () {
    return {
      rowPerPage: [30, 50, {text: this.$t('all'), value: -1}],
      pagination: {
        sortBy: 'authorizationId'
      },
      hazmatWarehouse: '',
      historyLimit: 20,
      showDatePick: false,
      sitesModel: {
        show: false,
        loading: false,
        warehouseKey: '',
        siteKey: '',
        authorizationId: '',
        sitesOptions: []
      }
    }
  },
  computed: {
    ...mapGetters([
      'hazmatSites',
      'warehouses'
    ]),
    abbrevToStates () {
      return this.$store.getters.abbrevToStates
    },
    headers () {
      return [
        { text: this.$t('name'), value: '', align: 'left', sortable: false, width: '5%' },
        { text: this.$t('authorizationId'), value: 'authorizationId', align: 'left', sortable: false, width: '5%' },
        { text: this.$t('address'), value: 'closeDate', align: 'left', sortable: false, width: '15%' },
        { text: `${this.$t('contact')}/${this.$t('phone')}`, value: '', align: 'left', sortable: false, width: '5%' },
        { text: this.$t('actions'), value: '', align: 'center', sortable: false, width: '2%' }
      ]
    },
    hazmatSitesOptions () {
      return this.hazmatSites.map(site => ({
        text: site.siteName,
        value: site.key
      }))
    },
    hazmatSitesTable () {
      if (!this.hazmatWarehouse) return this.hazmatSites
      return this.hazmatSites.filter(site => site.warehouseKey === this.hazmatWarehouse)
    },
    warehousesOptions () {
      return this.warehouses.map(warehouse => ({
        value: warehouse.warehouseKey,
        text: warehouse.warehouseName
      }))
    },
    allWarehousesOptions () {
      return [{
        value: '',
        text: this.$t('all')
      }, ...this.warehousesOptions]
    }
  },
  methods: {
    async updateAuthorizationSites (item) {
      try {
        await this.$store.dispatch('updateAuthorizationSites', item)
      } catch (err) {
        console.log(err)
        alert(err)
      }
    },
    addAuthorizationSites () {
      this.sitesModel.show = true
    },
    async deleteAuthorizationSites (item) {
      try {
        if (!confirm(this.$t('confirmDeleteAuthorizationSites'))) return
        await this.$store.dispatch('deleteAuthorizationSites', {
          siteKey: item.key,
          warehouseKey: item.warehouseKey
        })
      } catch (err) {
        let msg = err
        if (msg === 'not exist') msg = this.$t('warehouseSiteNotExist')
        console.log(msg)
        alert(msg)
      }
    },
    clearSiteModal () {
      Object.assign(this.sitesModel, {
        show: false,
        warehouseKey: '',
        siteKey: '',
        authorizationId: ''
      })
    },
    async submit () {
      try {
        this.sitesModel.loading = true
        const {
          siteKey,
          warehouseKey,
          authorizationId
        } = this.sitesModel
        const warehouseInfo = this.warehousesOptions.find(site => site.value === warehouseKey)
        const siteInfo = this.sitesModel.sitesOptions.find(site => site.value === siteKey)
        if (!warehouseInfo || !siteInfo) return
        await this.$store.dispatch('addAuthorizationSites', {
          siteKey,
          warehouseKey,
          warehouseName: warehouseInfo.text,
          siteInfo: siteInfo.data,
          authorizationId
        })
        this.clearSiteModal()
      } catch (err) {
        let msg = err
        if (msg === 'exist') msg = this.$t('warehouseSiteExist')
        console.log(msg)
        alert(msg)
      } finally {
        this.sitesModel.loading = false
      }
    },
    async getSitesOptions () {
      try {
        Object.assign(this.sitesModel, {
          loading: true,
          siteKey: '',
          sitesOptions: await this.$store.dispatch('getWarehouseSites', this.sitesModel.warehouseKey)
        })
      } catch (err) {
        console.log(err)
        this.sitesModel.sitesOptions = []
      } finally {
        this.sitesModel.loading = false
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/HazmatAuthorizationSites.vue